import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { MAX_RENDERED_RIDERS, type RideStore, type RiderVisual } from "../ride/store";
import { KIT_COLORS, SELF_ACCENT } from "./palette";

type Point = readonly [number, number, number];

function tube(radius: number, from: Point, to: Point, radialSegments = 5): THREE.BufferGeometry {
  const start = new THREE.Vector3(from[0], from[1], from[2]);
  const end = new THREE.Vector3(to[0], to[1], to[2]);
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  const geometry = new THREE.CylinderGeometry(radius, radius, length, radialSegments, 1, false);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    direction.normalize(),
  );
  geometry.applyQuaternion(quaternion);
  const midpoint = start.add(end).multiplyScalar(0.5);
  geometry.translate(midpoint.x, midpoint.y, midpoint.z);
  return geometry;
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = mergeGeometries(parts, false);
  if (!merged) {
    // Fall back to the first part rather than crashing the scene.
    return parts[0] ?? new THREE.BufferGeometry();
  }
  for (const part of parts) {
    if (part !== merged) part.dispose();
  }
  return merged;
}

function buildRiderGeometries(): {
  torso: THREE.BufferGeometry;
  helmet: THREE.BufferGeometry;
  head: THREE.BufferGeometry;
  arms: THREE.BufferGeometry;
  legs: THREE.BufferGeometry;
  frame: THREE.BufferGeometry;
  wheel: THREE.BufferGeometry;
  shadow: THREE.BufferGeometry;
  ring: THREE.BufferGeometry;
  marker: THREE.BufferGeometry;
} {
  const torso = new THREE.BoxGeometry(0.36, 0.5, 0.24);
  torso.rotateX(-0.42);
  torso.translate(0, 1.23, 0.02);

  const helmet = new THREE.SphereGeometry(0.155, 8, 5);
  helmet.scale(1, 0.94, 1.08);
  helmet.translate(0, 1.65, 0.19);

  const head = new THREE.SphereGeometry(0.11, 7, 5);
  head.scale(1, 1.06, 1);
  head.translate(0, 1.57, 0.22);

  const arms = merge([
    tube(0.035, [-0.16, 1.39, 0.12], [-0.21, 1.02, 0.48]),
    tube(0.035, [0.16, 1.39, 0.12], [0.21, 1.02, 0.48]),
  ]);

  const legs = merge([
    tube(0.055, [-0.11, 1.0, -0.14], [-0.13, 0.46, 0.04], 6),
    tube(0.055, [0.11, 1.0, -0.14], [0.13, 0.46, 0.04], 6),
  ]);

  const frameParts: THREE.BufferGeometry[] = [
    tube(0.028, [0, 0.35, -0.6], [0, 0.96, -0.18]),
    tube(0.028, [0, 0.96, -0.18], [0, 0.46, 0.02]),
    tube(0.028, [0, 0.46, 0.02], [0, 1.0, 0.47]),
    tube(0.028, [0, 0.46, 0.02], [0, 0.35, -0.6]),
    tube(0.025, [0, 1.0, 0.47], [0, 0.35, 0.6]),
  ];
  const handlebar = new THREE.BoxGeometry(0.46, 0.05, 0.05);
  handlebar.translate(0, 1.02, 0.49);
  const saddle = new THREE.BoxGeometry(0.16, 0.06, 0.3);
  saddle.translate(0, 0.99, -0.2);
  frameParts.push(handlebar, saddle);
  const frame = merge(frameParts);

  const wheel = new THREE.TorusGeometry(0.34, 0.033, 5, 10);
  // TorusGeometry lies in the XY plane (axle along Z). Rotate it into the
  // bike's YZ plane so the wheel is edge-on from behind and spins around X.
  wheel.rotateY(Math.PI / 2);
  const shadow = new THREE.CircleGeometry(0.95, 14);
  shadow.rotateX(-Math.PI / 2);
  const ring = new THREE.RingGeometry(0.82, 1.06, 28);
  ring.rotateX(-Math.PI / 2);
  const marker = new THREE.OctahedronGeometry(0.26, 0);

  return { torso, helmet, head, arms, legs, frame, wheel, shadow, ring, marker };
}

function buildMaterials(): {
  kit: THREE.MeshLambertMaterial;
  skin: THREE.MeshLambertMaterial;
  dark: THREE.MeshLambertMaterial;
  legs: THREE.MeshLambertMaterial;
  wheel: THREE.MeshLambertMaterial;
  shadow: THREE.MeshBasicMaterial;
  ring: THREE.MeshBasicMaterial;
  marker: THREE.MeshBasicMaterial;
} {
  return {
    kit: new THREE.MeshLambertMaterial({ color: "#ffffff" }),
    skin: new THREE.MeshLambertMaterial({ color: "#d5a47b" }),
    dark: new THREE.MeshLambertMaterial({ color: "#1d252c" }),
    legs: new THREE.MeshLambertMaterial({ color: "#26323b" }),
    wheel: new THREE.MeshLambertMaterial({ color: "#13171c" }),
    shadow: new THREE.MeshBasicMaterial({
      color: "#04110b",
      transparent: true,
      opacity: 0.26,
      depthWrite: false,
    }),
    ring: new THREE.MeshBasicMaterial({
      color: SELF_ACCENT,
      transparent: true,
      opacity: 0.86,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    marker: new THREE.MeshBasicMaterial({ color: "#ffffff", toneMapped: false }),
  };
}

function activeColor(visual: RiderVisual, palette: THREE.Color[], accent: THREE.Color): THREE.Color {
  if (visual.isSelf) return accent;
  return palette[Math.abs(visual.kit) % palette.length] ?? accent;
}

/**
 * All riders share a small set of instanced meshes. Twenty riders cost a
 * handful of draw calls, not twenty scene graphs.
 */
export function Riders({ store }: { store: RideStore }) {
  const geometries = useMemo(buildRiderGeometries, []);
  const materials = useMemo(buildMaterials, []);
  const palette = useMemo(() => KIT_COLORS.map((color) => new THREE.Color(color)), []);
  const accent = useMemo(() => new THREE.Color(SELF_ACCENT), []);

  const torsoRef = useRef<THREE.InstancedMesh>(null);
  const helmetRef = useRef<THREE.InstancedMesh>(null);
  const headRef = useRef<THREE.InstancedMesh>(null);
  const armsRef = useRef<THREE.InstancedMesh>(null);
  const legsRef = useRef<THREE.InstancedMesh>(null);
  const frameRef = useRef<THREE.InstancedMesh>(null);
  const wheelRef = useRef<THREE.InstancedMesh>(null);
  const shadowRef = useRef<THREE.InstancedMesh>(null);
  const markerRef = useRef<THREE.InstancedMesh>(null);
  const ringRef = useRef<THREE.Mesh>(null);

  useEffect(() => {
    for (const geometry of Object.values(geometries)) geometry.dispose();
    for (const material of Object.values(materials)) material.dispose();
  }, [geometries, materials]);

  const bodyDummy = useMemo(() => new THREE.Object3D(), []);
  const wheelDummy = useMemo(() => new THREE.Object3D(), []);

  useFrame(() => {
    const now = Date.now();
    const riders = store.collectRiders(now);
    const count = riders.length;
    const torso = torsoRef.current;
    const helmet = helmetRef.current;
    const head = headRef.current;
    const arms = armsRef.current;
    const legs = legsRef.current;
    const frame = frameRef.current;
    const wheels = wheelRef.current;
    const shadows = shadowRef.current;
    const markers = markerRef.current;
    const ring = ringRef.current;
    if (
      !torso ||
      !helmet ||
      !head ||
      !arms ||
      !legs ||
      !frame ||
      !wheels ||
      !shadows ||
      !markers
    ) {
      return;
    }

    torso.count = count;
    helmet.count = count;
    head.count = count;
    arms.count = count;
    legs.count = count;
    frame.count = count;
    wheels.count = count * 2;
    shadows.count = count;
    markers.count = count;

    for (let index = 0; index < count; index += 1) {
      const rider = riders[index];
      const bob = Math.sin(now * 0.006 + rider.id * 0.7) * 0.012;
      const bodyY = rider.pose.y + 0.02 + bob;

      bodyDummy.position.set(rider.pose.x, bodyY, rider.pose.z);
      bodyDummy.rotation.set(0, rider.pose.heading, 0);
      bodyDummy.scale.setScalar(1);
      bodyDummy.updateMatrix();

      torso.setMatrixAt(index, bodyDummy.matrix);
      helmet.setMatrixAt(index, bodyDummy.matrix);
      head.setMatrixAt(index, bodyDummy.matrix);
      arms.setMatrixAt(index, bodyDummy.matrix);
      legs.setMatrixAt(index, bodyDummy.matrix);
      frame.setMatrixAt(index, bodyDummy.matrix);

      const colour = activeColor(rider, palette, accent);
      torso.setColorAt(index, colour);
      helmet.setColorAt(index, colour);
      markers.setColorAt(index, colour);

      const sin = Math.sin(rider.pose.heading);
      const cos = Math.cos(rider.pose.heading);
      const spin = (now / 1000) * (rider.speed / 0.34);
      for (let wheelIndex = 0; wheelIndex < 2; wheelIndex += 1) {
        const offset = wheelIndex === 0 ? 0.6 : -0.6;
        wheelDummy.position.set(
          rider.pose.x + sin * offset,
          rider.pose.y + 0.35,
          rider.pose.z + cos * offset,
        );
        wheelDummy.rotation.set(spin, rider.pose.heading, 0, "YXZ");
        wheelDummy.scale.setScalar(1);
        wheelDummy.updateMatrix();
        wheels.setMatrixAt(index * 2 + wheelIndex, wheelDummy.matrix);
      }

      bodyDummy.position.set(rider.pose.x, rider.pose.y + 0.035, rider.pose.z);
      bodyDummy.rotation.set(0, rider.pose.heading, 0);
      bodyDummy.updateMatrix();
      shadows.setMatrixAt(index, bodyDummy.matrix);

      bodyDummy.position.set(rider.pose.x, rider.pose.y + 2.18 + bob, rider.pose.z);
      bodyDummy.rotation.set(0, now * 0.0012 + rider.id, 0);
      // The local rider already has the bright ground ring; a floating marker
      // is for reading the pack at distance without cluttering your own view.
      bodyDummy.scale.setScalar(rider.isSelf ? 0.001 : 1);
      bodyDummy.updateMatrix();
      markers.setMatrixAt(index, bodyDummy.matrix);

      if (rider.isSelf && ring) {
        ring.position.set(rider.pose.x, rider.pose.y + 0.045, rider.pose.z);
        ring.scale.setScalar(1 + Math.sin(now * 0.005) * 0.06);
      }
    }

    for (const mesh of [torso, helmet, head, arms, legs, frame, wheels, shadows, markers]) {
      mesh.instanceMatrix.needsUpdate = true;
    }
    if (torso.instanceColor) torso.instanceColor.needsUpdate = true;
    if (helmet.instanceColor) helmet.instanceColor.needsUpdate = true;
    if (markers.instanceColor) markers.instanceColor.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh
        ref={torsoRef}
        args={[geometries.torso, materials.kit, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={helmetRef}
        args={[geometries.helmet, materials.kit, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={headRef}
        args={[geometries.head, materials.skin, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={armsRef}
        args={[geometries.arms, materials.skin, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={legsRef}
        args={[geometries.legs, materials.legs, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={frameRef}
        args={[geometries.frame, materials.dark, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={wheelRef}
        args={[geometries.wheel, materials.wheel, MAX_RENDERED_RIDERS * 2]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={shadowRef}
        args={[geometries.shadow, materials.shadow, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={markerRef}
        args={[geometries.marker, materials.marker, MAX_RENDERED_RIDERS]}
        frustumCulled={false}
      />
      <mesh ref={ringRef} geometry={geometries.ring} material={materials.ring} renderOrder={4} />
    </group>
  );
}
